import { isAreaPriced } from './marineService.js';

/**
 * Pull the pricing configuration off a master service and its price rows.
 *
 * Field-by-field construction has silently lost marine fields three times
 * in this codebase, so this is the single place the shape is assembled.
 *
 * `pricingType`:
 *   'area'  — `pricing_kind='area'`, or (pre-migration) any SF row / a formula
 *   'flat'  — `pricing_kind='unit'` AND an SF row (spec 2026-09-11 D7)
 *   'size'  — everything else
 *
 * Nothing here keys on `asset_type`: an area-priced automotive service must
 * read as area, and a By-size marine service must read as size.
 *
 * Zero UI imports — tested by `scripts/test/areaConfig.test.mjs`.
 */
export const extractAreaConfig = (service) => {
  const svc = service || {};
  const rows = Array.isArray(svc.pricing) ? svc.pricing : [];
  const sf = rows.filter((r) => String(r.vehicle_size || '').toUpperCase() === 'SF');
  const hasSf = sf.length > 0;
  const kind = String(svc.pricing_kind ?? '').trim().toLowerCase();

  const isArea = kind
    ? kind === 'area'
    : (hasSf || svc.area_formula != null || isAreaPriced(svc));
  const isFlat = !isArea && hasSf;
  const pricingType = isArea ? 'area' : isFlat ? 'flat' : 'size';

  const retail = sf.find((r) => r.catalog === 'RETAIL') || null;
  const whole = sf.find((r) => r.catalog === 'WHOLE') || null;
  // '' is absent, same as null/undefined — Number('') is 0, which would
  // otherwise read as a real, deliberate zero rather than "not set".
  const numOrEmpty = (v) => {
    if (v === '' || v === null || v === undefined) return '';
    const n = Number(v);
    return Number.isFinite(n) ? n : '';
  };
  const priceOf = (row) => (row ? numOrEmpty(row.price ?? row.service_cost) : '');
  const unit = String(svc.unit_of_measure ?? '').trim().toLowerCase();

  return {
    isArea,
    isFlat,
    pricingType,
    assetType: svc.asset_type || 'auto',
    unitOfMeasure: (unit === 'ft' || unit === 'sq_ft' || unit === 'each') ? unit : (isArea ? 'sq_ft' : 'each'),
    areaFactor: svc.area_factor ?? '',
    wastePct: svc.waste_pct ?? '',
    areaFormula: svc.area_formula ?? null,
    retailRate: priceOf(retail),
    wholesaleRate: priceOf(whole),
    durationDays: retail ? numOrEmpty(retail.duration_days) : '',
  };
};
